import {
  Button,
  Divider,
  Icon,
  Spinner,
  Typography,
} from "@mindlercare/ui-web";
import { PropsWithChildren, ReactNode, useLayoutEffect, useRef } from "react";
import { styled } from "styled-components";

import { useIcbtTranslation } from "../translations";

import { IcbtPage } from "../IcbtPage";
import type { ContentfulEntryLink, PageWithContent } from "../types";

export type ProgramPreviewColumnProps = PropsWithChildren<{
  title?: string;
  isLoading?: boolean;
  error?: unknown;
  onExpandPreview?: () => void;
}>;

export const ProgramPreviewColumn = ({
  title,
  isLoading,
  error,
  onExpandPreview,
  children,
}: ProgramPreviewColumnProps) => {
  const t = useIcbtTranslation();

  if (isLoading) {
    return (
      <ProgramPreviewWrapper>
        <Spinner />
      </ProgramPreviewWrapper>
    );
  }

  if (error) {
    return (
      <ProgramPreviewWrapper>
        <Typography variant="h3" insensitive>
          {isHttpError(error, 403)
            ? t.ICBT_LOADING_DATA_ERROR_FORBIDDEN
            : t.ICBT_LOADING_DATA_ERROR}
        </Typography>
      </ProgramPreviewWrapper>
    );
  }

  if (!title) return null;

  return (
    <ProgramPreviewWrapper>
      <HeaderRow>
        <Typography
          variant="body-small"
          color="GRAY_DARK"
          sx={{ paddingLeft: "16px" }}
          insensitive
        >
          {t.ICBT_PROGRAM_PAGE_BY_PAGE}
        </Typography>
        {onExpandPreview && (
          <ExpandPreviewButton
            variant="icon"
            onClick={() => onExpandPreview?.()}
          >
            <Icon.OpenInFull aria-label="expand-program-content" />
          </ExpandPreviewButton>
        )}
      </HeaderRow>
      <Typography
        variant="h3"
        sx={{ paddingLeft: "16px", paddingBottom: "16px" }}
        dataTestId="programPreviewTitle"
        insensitive
      >
        {title}
      </Typography>
      <SingleProgramContainer>{children}</SingleProgramContainer>
    </ProgramPreviewWrapper>
  );
};

export const ProgramPages = ({
  pages,
  enableAudio,
  enableVideo,
  currentProgressPageIndex,
  renderQuestionnaire,
  renderPatientNote,
  overrideMaxWidth,
}: {
  pages: PageWithContent[];
  enableAudio?: boolean;
  enableVideo?: boolean;
  currentProgressPageIndex?: number;
  renderQuestionnaire?: (entry: ContentfulEntryLink) => ReactNode;
  renderPatientNote?: (entry: ContentfulEntryLink) => ReactNode;
  overrideMaxWidth?: string;
}) => {
  const t = useIcbtTranslation();
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

  useLayoutEffect(() => {
    if (
      currentProgressPageIndex !== undefined &&
      pageRefs.current[currentProgressPageIndex]
    ) {
      pageRefs.current[currentProgressPageIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [currentProgressPageIndex]);

  return (
    <ProgramContainer $overrideMaxWidth={overrideMaxWidth}>
      {pages.map((page, i) => (
        <div key={page.id}>
          <IcbtPage
            ref={(el) => {
              pageRefs.current[i] = el;
            }}
            page={page}
            enableAudio={enableAudio}
            enableVideo={enableVideo}
            isCurrentProgress={currentProgressPageIndex === i}
            renderQuestionnaire={renderQuestionnaire}
            renderPatientNote={renderPatientNote}
          />
          <PageNumber>
            {i + 1} {t.OF} {pages.length}
          </PageNumber>
          {i < pages.length - 1 && <Divider />}
        </div>
      ))}
    </ProgramContainer>
  );
};

const ProgramPreviewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  height: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const ExpandPreviewButton = styled(Button)``;

const SingleProgramContainer = styled.div`
  overflow-y: auto;
`;

const ProgramContainer = styled.div<{ $overrideMaxWidth?: string }>`
  max-width: ${({ $overrideMaxWidth }) => $overrideMaxWidth ?? "720px"};
  padding: 0 5px;
`;

const PageNumber = styled.span`
  display: flex;
  justify-content: center;
  color: ${({ theme }) => theme.COLORS.GRAY};
  padding: ${({ theme }) => theme.PADDING.MD} 0;
`;

const isHttpError = (e: unknown, statusCode: number): boolean => {
  if (!e || typeof e !== "object") return false;
  const obj = e as Record<string, unknown>;
  if (obj.response && typeof obj.response === "object") {
    const response = obj.response as Record<string, unknown>;
    if (response.status === statusCode) return true;
  }
  if (obj.status === statusCode) return true;
  return false;
};
